// @flow
import * as React from 'react';
import Modal from 'react-modal';
import idx from 'idx';

import { withRouter } from 'app/context/Router';
import type { Router } from 'react-router';
import {
  setAutoMessageStatus,
  duplicateAutoMessage,
  deleteAutoMessage,
} from 'admin/actions/AutoMessageActions';
import { statusToTextBadge } from 'admin/constants/AutoMessages';
import AdminAutoMessagesNull from './AdminAutoMessagesNull';
import AdminAutoMessagesDeleteConfirmDialog from './AdminAutoMessagesDeleteConfirmDialog';
import AdminNewAutoMessageModal from 'admin/components/AdminNewAutoMessage';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import {
  ComposableTable,
  composeMultipleCells,
  getErrorTextCellGenerator,
  getMenuButtonCellGenerator,
  getTextCellGenerator,
  getTimestampCellGenerator,
  getTextBadgeCellGenerator,
} from 'components/ComposableTable';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import type {
  AdminAutoMessages_organizations,
  SaveAutoMessageInput,
} from './queries/__generated__/AdminAutoMessages';

type Props = {
  loading: ?boolean,
  refetchAutoMessagesForOrg: ?() => void,
  organization: AdminAutoMessages_organizations,
  saveAutoMessage: (input: SaveAutoMessageInput) => Promise<*>,
  onClickNewAutoMessage: () => void,
  urlSlug: string,
  viewingOrgUuid: string,
  router: Router,
};

type State = {
  isConfirmDeleteDialogOpened: boolean,
  isNewAutoMessageModalOpened: boolean,
  autoMessageModalMessageUuid: ?string,
  autoMessageModalStartingTab: ?*,
  deleteAutoMessageUuid: ?string,
  deleteAutoMessageName: ?string,
};

class AdminAutoMessagesView extends React.Component<Props, State> {
  state = {
    isNewAutoMessageModalOpened: false,
    isConfirmDeleteDialogOpened: false,
    autoMessageModalMessageUuid: undefined,
    autoMessageModalStartingTab: undefined,
    deleteAutoMessageUuid: undefined,
    deleteAutoMessageName: undefined,
  };

  showAutoMessageDialog = (
    autoMessageModalMessageUuid: string,
    autoMessageModalStartingTab?: *
  ) => {
    this.setState(
      {
        autoMessageModalMessageUuid,
        autoMessageModalStartingTab,
      },
      () => this.setState({ isNewAutoMessageModalOpened: true })
    );
  };

  hideAutoMessageDialog = () => {
    this.setState({
      autoMessageModalStartingTab: undefined,
      autoMessageModalMessageUuid: undefined,
      isNewAutoMessageModalOpened: false,
    });
  };

  showConfirmDeleteDialog = (deleteAutoMessageUuid: string, deleteAutoMessageName: string) => {
    this.setState(
      {
        deleteAutoMessageUuid,
        deleteAutoMessageName,
      },
      () => this.setState({ isConfirmDeleteDialogOpened: true })
    );
  };

  hideConfirmDeleteDialog = () => {
    this.setState({
      deleteAutoMessageUuid: undefined,
      deleteAutoMessageName: undefined,
      isConfirmDeleteDialogOpened: false,
    });
  };

  handleDelete = () => {
    const { deleteAutoMessageUuid, deleteAutoMessageName } = this.state;
    if (deleteAutoMessageUuid) {
      deleteAutoMessage(
        deleteAutoMessageUuid,
        deleteAutoMessageName || 'This message',
        this.props.organization.uuid,
        () => undefined,
        () => this.hideConfirmDeleteDialog()
      );
      return;
    }
    console.error('AdminAutoMessagesView - tried to delete an auto message without a uuid');
    ToastActions.showErrorToast('Oops! Looks like something went wrong.');
  };

  goToMessageDetail = (uuid: string) => {
    if (this.props.router) {
      this.props.router.push(`${this.props.urlSlug}/${uuid}`);
    }
  };

  formatStudentAndParentLabel = (includeParents: boolean, includeStudents: boolean) => {
    const parentText = includeParents ? 'Parents and guardians' : null;
    const studentText = includeStudents ? 'Students' : null;
    return [studentText, parentText].filter(text => text).join(', ');
  };

  renderEmptySubtextComponent = (
    canEdit: boolean,
    dataTestKey: string,
    messageUuid: string,
    step: *,
    buttonLabel: string,
    nonButtonLabel: string
  ) => {
    const openModalAction = e => {
      e.stopPropagation();
      this.showAutoMessageDialog(messageUuid, step);
    };
    if (canEdit) {
      return (
        <Button
          data-test={dataTestKey}
          level="icon"
          color="brand"
          onClick={e => openModalAction(e)}>
          {buttonLabel}
        </Button>
      );
    }
    return <Text italic>{nonButtonLabel}</Text>;
  };

  formatDataIntoRow = (message: *) => {
    const {
      isActivatable,
      lastSend,
      messageName,
      messageContent,
      csvName,
      creatorOrganization,
      organization,
      includeParents,
      includeStudents,
      status,
      sender,
      uuid,
      customMessageFields,
    } = message;

    const canEdit = creatorOrganization ? creatorOrganization.isAdmin : false;
    const lastSendTime = idx(lastSend, _ => _.sentAt);
    const lastSendErrorCount =
      (idx(lastSend, _ => _.readSummary.unreachableEntry.count) || 0) +
      (idx(lastSend, _ => _.readSummary.identificationErrorEntry.count) || 0);
    const lastSendErrorLabel =
      lastSendErrorCount > 0 ? `${lastSendErrorCount} errors` : 'No errors';

    return {
      uuid,
      messageName,
      includeParents,
      includeStudents,
      csvName:
        csvName ||
        this.renderEmptySubtextComponent(
          canEdit,
          'empty-csv-name-button',
          uuid,
          'format-csv',
          'Add filename',
          'Filename not yet defined'
        ),
      organization,
      orgName: organization.name,
      recipientLabel:
        this.formatStudentAndParentLabel(includeParents, includeStudents) ||
        this.renderEmptySubtextComponent(
          canEdit,
          'empty-recipients-button',
          uuid,
          'compose',
          'Assign message recipients',
          'Recipients not yet selected'
        ),
      sender,
      status,
      messageContent,
      lastSendTime,
      lastSendErrorCount,
      lastSendErrorLabel: lastSendTime ? lastSendErrorLabel : 'No sends yet',
      canEdit,
      canActivate: isActivatable && status !== 'active',
      canPause: status === 'active',
      customMessageFields,
    };
  };

  render() {
    const { loading, organization, onClickNewAutoMessage } = this.props;

    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (!organization) {
      return (
        <ErrorReporter error="AdminAutoMessagesView - tried to render without an organization" />
      );
    }
    const { childOrganizations } = organization;

    if (organization && organization.autoMessages.length === 0) {
      // TODO: move logic to apollo
      const noOrgsWithGoodStudentIdPercentage =
        organization.studentIdPercentage < 0.01 &&
        !childOrganizations.some(org => org && org.studentIdPercentage > 0.01);
      return (
        <AdminAutoMessagesNull
          showStudentIdPercentageWarning={noOrgsWithGoodStudentIdPercentage}
          onClickNewAutoMessage={onClickNewAutoMessage}
        />
      );
    }

    const columnHeaders = ['Name', 'Status', 'Recipients', 'Last sent', ''].map(name => ({ name }));

    const columnGenerators = [
      composeMultipleCells([
        getTextCellGenerator('messageName', {
          lineHeight: 'xlarge',
          size: 'small',
          weight: 'semibold',
          element: 'div',
        }),
        getTextCellGenerator('csvName', { color: 'thunder', size: 'xxsmall', element: 'div' }),
      ]),
      getTextBadgeCellGenerator('status', statusToTextBadge),
      composeMultipleCells([
        getTextCellGenerator('orgName', {
          lineHeight: 'xlarge',
          size: 'small',
          element: 'div',
        }),
        getTextCellGenerator('recipientLabel', {
          color: 'thunder',
          size: 'xxsmall',
          element: 'div',
        }),
      ]),
      composeMultipleCells([
        getTimestampCellGenerator('lastSendTime', {
          lineHeight: 'xlarge',
          size: 'small',
          element: 'div',
        }),
        getErrorTextCellGenerator('lastSendErrorLabel', 'lastSendErrorCount', { color: 'thunder' }),
      ]),
      getMenuButtonCellGenerator([
        {
          conditionToCheck: 'canActivate',
          label: 'Activate',
          icon: 'play-circle',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: data =>
            setAutoMessageStatus(data.uuid, 'active', organization.uuid, data.messageName),
        },
        {
          conditionToCheck: 'canPause',
          label: 'Pause',
          icon: 'pause-circle',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: data =>
            setAutoMessageStatus(data.uuid, 'inactive', organization.uuid, data.messageName),
        },
        {
          conditionToCheck: 'canEdit',
          label: 'Edit',
          glyph: 'pencil-stroke',
          fieldsForAction: ['uuid'],
          onClickAction: data => this.showAutoMessageDialog(data.uuid),
        },
        {
          conditionToCheck: undefined,
          label: 'Duplicate',
          icon: 'copy',
          fieldsForAction: [
            'messageName',
            'organization',
            'includeParents',
            'includeStudents',
            'messageContent',
            'sender',
            'customMessageFields',
          ],
          onClickAction: data => duplicateAutoMessage(data, organization.uuid),
        },
        {
          conditionToCheck: undefined,
          label: 'View sent messages',
          glyph: 'email-transcript',
          fieldsForAction: ['uuid'],
          onClickAction: data => this.goToMessageDetail(data.uuid),
        },
        {
          conditionToCheck: 'canEdit',
          label: 'Delete',
          glyph: 'trash',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: data => this.showConfirmDeleteDialog(data.uuid, data.messageName),
          divided: true,
        },
      ]),
    ];

    const rows = organization.autoMessages.map(message => this.formatDataIntoRow(message));

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isNewAutoMessageModalOpened}>
          <AdminNewAutoMessageModal
            onBack={this.hideAutoMessageDialog}
            viewingOrgUuid={organization.uuid}
            messageUuid={this.state.autoMessageModalMessageUuid}
            startingTab={this.state.autoMessageModalStartingTab}
          />
        </Modal>
        <Modal isOpen={this.state.isConfirmDeleteDialogOpened}>
          <AdminAutoMessagesDeleteConfirmDialog
            messageName={this.state.deleteAutoMessageName}
            onConfirm={this.handleDelete}
            onRequestHide={this.hideConfirmDeleteDialog}
          />
        </Modal>
        <Row size="small">
          <MediaObject figure="right" center>
            <Heading flush>Auto messages</Heading>
            <Button data-test="new-auto-message-button" onClick={onClickNewAutoMessage}>
              New auto message
            </Button>
          </MediaObject>
        </Row>
        <ComposableTable
          columnHeaders={columnHeaders}
          columnGenerators={columnGenerators}
          rows={rows}
          keyField="uuid"
          actionsColumn
          handleRowClick={row => this.goToMessageDetail(row.uuid)}
        />
      </React.Fragment>
    );
  }
}

export default withRouter(AdminAutoMessagesView);
